import { adultProfile, childProfile, teenProfile } from './registry';
import type { Persona, PersonaProfile } from './types';

/**
 * Resultatet av att resolva en KLIENTS persona i handledarläge (skild från
 * useActivePersona, som resolvar BRUKARENS egen vy). Handledaren styr en klient
 * och belöningsadmin (Stjärnbutik/Stjärnmål) ska gata på klientens FAKTISKA
 * persona — inte på handledarens (som alltid är 'adult' i handledarläge).
 */
export type ClientPersonaResolution = {
  /** Profilen vars features/tema gäller för klienten. Vid fallback: adultProfile. */
  profile: PersonaProfile;
  /** Klientens kända persona, eller null när den saknas/är okänd. */
  persona: Persona | null;
  /**
   * true när klientens persona saknas/är okänd. Då DÖLJS belöningsadmin (vi
   * defaultar ALDRIG till barn — det skulle tyst aktivera stjärnor för fel
   * ålder). Designen kräver i stället en medveten 3-vals-bekräftelse
   * (child/teen/adult) innan belöning slås på.
   */
  isFallback: boolean;
};

/**
 * Slås upp vid ANROP, aldrig vid modulladdning. registry.ts importerar
 * teen-vyerna, och TeenHome leder via usePendingStarValues → useStarValuePersona
 * → useClientPersona tillbaka hit. En toppnivåläsning av childProfile hamnar då
 * i TDZ medan registry.ts fortfarande evalueras och kraschar hela appen vid
 * start ("Cannot access 'childProfile' before initialization"). Inne i
 * funktionen är registret färdigt.
 */
const profileFor = (persona: Persona): PersonaProfile => {
  const profiles: Record<Persona, PersonaProfile> = {
    child: childProfile,
    teen: teenProfile,
    adult: adultProfile,
  };
  return profiles[persona];
};

const isKnownPersona = (value: unknown): value is Persona =>
  value === 'child' || value === 'teen' || value === 'adult';

/**
 * Ren resolver (inga hooks, inga sidoeffekter) — testbar likt resolveActiveProfile.
 *
 *   - Känd persona → motsvarande profil, isFallback=false.
 *   - Saknad/okänd persona → adultProfile (stars av = belöning dold),
 *     persona=null, isFallback=true.
 */
export const resolveClientPersona = (
  persona: Persona | null | undefined,
): ClientPersonaResolution => {
  if (isKnownPersona(persona)) {
    return { profile: profileFor(persona), persona, isFallback: false };
  }
  return { profile: adultProfile, persona: null, isFallback: true };
};
